// Pure geometry/grouping helpers for the "move and resize media" feature
// (see PageSlider.js). Kept dependency-free and side-effect-free so they're
// cheap to call on every pointer-move and easy to unit test directly.

// The page's permitted editing area, per the design's UserGuide: the dark
// blue trim strip around non-bleed pages blocks media from entering it, and
// the same margins apply to bleed pages too (no separate page-dimension
// source for a bleed-specific inset exists, so one set of margins covers
// both). All values are in the same native coordinate units as a layer's
// xpos/ypos/width/height — not on-screen (zoomed) pixels.
export const PAGE_MARGINS = { left: 70, right: 70, top: 59, bottom: 59 };

export const getGroupId = layer => layer?.['group-id'] ?? null;

// Groups a flat list of top-level layers by `group-id`. Layers with no
// group-id are their own single-member group. Order of `layers` is preserved
// within each group; group order follows first-appearance in `layers`.
export const groupLayers = (layers = []) => {
  const groups = [];
  const byKey = new Map();

  layers.forEach(layer => {
    const groupId = getGroupId(layer);
    const key = groupId || `layer:${layer.id}`;
    if (!byKey.has(key)) {
      const group = { key, groupId, layerIds: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    byKey.get(key).layerIds.push(layer.id);
  });
  return groups;
};

// Returns every layer id that belongs to the same group as `layerId`
// (including itself). A layer with no group-id is a group of one.
export const findGroupLayerIds = (layers = [], layerId) => {
  const target = layers.find(layer => layer.id === layerId);
  if (!target) return [];
  const groupId = getGroupId(target);
  if (!groupId) return [layerId];
  return layers.filter(layer => getGroupId(layer) === groupId).map(layer => layer.id);
};

// Union bounding box of a set of rects ({ xpos, ypos, width, height }).
export const getBoundingBox = rects => {
  if (!rects.length) return null;
  const left = Math.min(...rects.map(r => r.xpos));
  const top = Math.min(...rects.map(r => r.ypos));
  const right = Math.max(...rects.map(r => r.xpos + r.width));
  const bottom = Math.max(...rects.map(r => r.ypos + r.height));
  return { xpos: left, ypos: top, width: right - left, height: bottom - top };
};

// Clamps a proposed (dx, dy) translation of `box` so it stays within the page
// margins. Returns the (possibly reduced) { dx, dy } to actually apply.
export const clampTranslate = (box, dx, dy, pageWidth, pageHeight, margins = PAGE_MARGINS) => {
  const minX = margins.left;
  const maxX = pageWidth - margins.right;
  const minY = margins.top;
  const maxY = pageHeight - margins.bottom;

  let clampedDx = dx;
  let clampedDy = dy;

  if (box.xpos + dx < minX) clampedDx = minX - box.xpos;
  if (box.xpos + box.width + dx > maxX) clampedDx = Math.min(clampedDx, maxX - (box.xpos + box.width));

  if (box.ypos + dy < minY) clampedDy = minY - box.ypos;
  if (box.ypos + box.height + dy > maxY) clampedDy = Math.min(clampedDy, maxY - (box.ypos + box.height));

  return { dx: clampedDx, dy: clampedDy };
};

// Clamps a proposed resize of `box` from a given corner handle so the result
// stays within the page margins and never collapses to a non-positive size.
// `handle` is one of 'nw' | 'ne' | 'sw' | 'se'.
export const clampResize = (box, handle, dx, dy, pageWidth, pageHeight, margins = PAGE_MARGINS) => {
  const minX = margins.left;
  const maxX = pageWidth - margins.right;
  const minY = margins.top;
  const maxY = pageHeight - margins.bottom;
  const MIN_SIZE = 20;

  let { xpos, ypos, width, height } = box;
  const movesLeft = handle === 'nw' || handle === 'sw';
  const movesTop = handle === 'nw' || handle === 'ne';

  let clampedDx = dx;
  let clampedDy = dy;

  if (movesLeft) {
    // Left edge moves; don't cross the right edge or the left margin.
    clampedDx = Math.min(clampedDx, width - MIN_SIZE);
    if (xpos + clampedDx < minX) clampedDx = minX - xpos;
  } else {
    // Right edge moves.
    clampedDx = Math.max(clampedDx, MIN_SIZE - width);
    if (xpos + width + clampedDx > maxX) clampedDx = maxX - (xpos + width);
  }

  if (movesTop) {
    clampedDy = Math.min(clampedDy, height - MIN_SIZE);
    if (ypos + clampedDy < minY) clampedDy = minY - ypos;
  } else {
    clampedDy = Math.max(clampedDy, MIN_SIZE - height);
    if (ypos + height + clampedDy > maxY) clampedDy = maxY - (ypos + height);
  }

  if (movesLeft) {
    xpos += clampedDx;
    width -= clampedDx;
  } else {
    width += clampedDx;
  }

  if (movesTop) {
    ypos += clampedDy;
    height -= clampedDy;
  } else {
    height += clampedDy;
  }

  return { xpos, ypos, width, height };
};

// Scales every member layer's rect proportionally to a new group bounding
// box, preserving each layer's relative position/size within the group
// (used when resizing a multi-layer group so members keep their original
// arrangement — e.g. an image with a caption underneath it).
export const scaleLayersToBox = (rectsById, fromBox, toBox) => {
  const scaleX = fromBox.width === 0 ? 1 : toBox.width / fromBox.width;
  const scaleY = fromBox.height === 0 ? 1 : toBox.height / fromBox.height;

  const next = {};
  Object.entries(rectsById).forEach(([id, rect]) => {
    next[id] = {
      xpos: toBox.xpos + (rect.xpos - fromBox.xpos) * scaleX,
      ypos: toBox.ypos + (rect.ypos - fromBox.ypos) * scaleY,
      width: rect.width * scaleX,
      height: rect.height * scaleY,
    };
  });
  return next;
};

// Translates every member layer's rect by the same (dx, dy).
export const translateLayers = (rectsById, dx, dy) => {
  const next = {};
  Object.entries(rectsById).forEach(([id, rect]) => {
    next[id] = { ...rect, xpos: rect.xpos + dx, ypos: rect.ypos + dy };
  });
  return next;
};
